/**
 * Determines whether the current user has assignment-based access to a
 * conversation.  Mirrors the backend predicate in
 * Conversations::PermissionFilterService / accessible_through_assignment:
 *
 *   (assignee IS NULL AND team IS NULL) OR assignee == me OR team ∈ my teams
 *
 * This is a UX-only guard — the backend is always the authoritative source of
 * truth.  Use it to avoid wrongly hiding conversations when the teams store
 * has not yet loaded: callers must pass the raw myTeamIds array and
 * short-circuit (skip removal) when it is empty and teams are still fetching.
 *
 * @param {object} conversation - Conversation object from the Vuex store.
 * @param {number|null} currentUserId - ID of the currently authenticated user.
 * @param {number[]} myTeamIds - Array of team IDs the current user is a member of.
 * @returns {boolean} true if the user should be able to see this conversation.
 */
export const hasAssignmentAccess = (conversation, currentUserId, myTeamIds) => {
  const assigneeId = conversation?.meta?.assignee?.id ?? null;
  const teamId = conversation?.meta?.team?.id ?? null;

  // Unassigned to any agent or team — visible to all inbox members.
  if (!assigneeId && !teamId) return true;

  // Assigned directly to this user.
  if (assigneeId === currentUserId) return true;

  // Assigned to a team this user belongs to.
  if (teamId && myTeamIds.includes(teamId)) return true;

  return false;
};
